import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Button,
    TablePagination,
    Slide,
    IconButton,
    Popover,
    CircularProgress,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {InfoOutlined, DeleteOutline, ExpandMore, ExpandLess} from '@mui/icons-material';
import {AuditHkfrsSmeFrsService} from '@/services/auditService';
import {SmeFrsVO, SmeFrsPage} from '@/services/auditService/auditHkfrsSmeFrsService';
import {SnackbarField, InputField} from '@/components/common';
import SmeFrsDetail from '@/pages/components/manage/dictionary/sme-frs/SmeFrsDetail';

/* ======================== 搜索模式：递归渲染预加载的子树 ======================== */

function SearchChildRows({children, depth, theme, onDetail, onRemove}: {
    children?: SmeFrsVO[]; depth: number; theme: any;
    onDetail: (id: string) => void;
    onRemove: (id: string) => void;
}) {
    if (!children || children.length === 0) return null;
    return (
        <>
            {children.map((child, idx) => (
                <React.Fragment key={child.id ?? `child-${depth}-${idx}`}>
                    <TableRow hover>
                        <TableCell sx={{pl: 2 + depth * 3}}>{child.no || '-'}</TableCell>
                        <TableCell>{child.content || '-'}</TableCell>
                        <TableCell>{(child.code ?? []).join(', ') || '-'}</TableCell>
                        <TableCell>
                            <Stack direction="row" spacing={0.5}>
                                {child.id && (
                                    <IconButton size="small" color="primary" onClick={() => onDetail(child.id!)}>
                                        <InfoOutlined fontSize="small"/>
                                    </IconButton>
                                )}
                                {child.id && (
                                    <DeletePopover nodeId={child.id}
                                                   nodeName={child.content || child.no || ''}
                                                   onRemove={onRemove} theme={theme}/>
                                )}
                            </Stack>
                        </TableCell>
                    </TableRow>
                    <SearchChildRows children={child.children} depth={depth + 1} theme={theme}
                                     onDetail={onDetail} onRemove={onRemove}/>
                </React.Fragment>
            ))}
        </>
    );
}

/* ======================== 懒加载模式：根据 childrenMap 渲染 ======================== */

function LazyChildRows({parentId, depth, theme, childrenMap, expandedIds, loadingIds, onToggle, onDetail, onRemove}: {
    parentId: string; depth: number; theme: any;
    childrenMap: Record<string, SmeFrsVO[]>;
    expandedIds: Set<string>;
    loadingIds: Set<string>;
    onToggle: (id: string) => void;
    onDetail: (id: string) => void;
    onRemove: (id: string) => void;
}) {
    const children = childrenMap[parentId];
    if (!children || children.length === 0) return null;
    return (
        <>
            {children.map((child, idx) => {
                const canExpand = child.hasChildren !== false;
                const isExpanded = child.id ? expandedIds.has(child.id) : false;
                const isLoading = child.id ? loadingIds.has(child.id) : false;
                return (
                    <React.Fragment key={child.id ?? `lazy-${depth}-${idx}`}>
                        <TableRow hover sx={{cursor: canExpand ? 'pointer' : 'default'}}
                                  onClick={() => child.id && canExpand && onToggle(child.id)}>
                            <TableCell sx={{pl: 2 + depth * 3}}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    {canExpand && (isLoading
                                        ? <CircularProgress size={16}/>
                                        : isExpanded ? <ExpandLess fontSize="small"/> :
                                            <ExpandMore fontSize="small"/>)}
                                    <span>{child.no || '-'}</span>
                                </Stack>
                            </TableCell>
                            <TableCell>{child.content || '-'}</TableCell>
                            <TableCell>{(child.code ?? []).join(', ') || '-'}</TableCell>
                            <TableCell>
                                <Stack direction="row" spacing={0.5} onClick={e => e.stopPropagation()}>
                                    {child.id && (
                                        <IconButton size="small" color="primary"
                                                    onClick={() => onDetail(child.id!)}>
                                            <InfoOutlined fontSize="small"/>
                                        </IconButton>
                                    )}
                                    {child.id && (
                                        <DeletePopover nodeId={child.id}
                                                       nodeName={child.content || child.no || ''}
                                                       onRemove={onRemove} theme={theme}/>
                                    )}
                                </Stack>
                            </TableCell>
                        </TableRow>
                        {child.id && isExpanded && (
                            <LazyChildRows parentId={child.id} depth={depth + 1} theme={theme}
                                           childrenMap={childrenMap} expandedIds={expandedIds}
                                           loadingIds={loadingIds} onToggle={onToggle}
                                           onDetail={onDetail} onRemove={onRemove}/>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    );
}

/* ======================== 删除确认 Popover ======================== */

function DeletePopover({nodeId, nodeName, onRemove, theme}: {
    nodeId: string; nodeName: string; onRemove: (id: string) => void; theme: any;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleConfirm = () => {
        onRemove(nodeId);
        handleClose();
    };
    return (
        <>
            <IconButton size="small" color="error" onClick={handleClick}>
                <DeleteOutline fontSize="small"/>
            </IconButton>
            <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Box p={2}>
                    <Typography variant="body2" sx={{mb: 1}}>Confirm to delete "{nodeName}"?</Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleClose}>Cancel</Button>
                        <Button size="small" color="error" variant="contained" onClick={handleConfirm}>Confirm</Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}

/* ======================== 主列表组件 ======================== */

export default function SmeFrsList() {
    const theme = useTheme();
    const [data, setData] = useState<SmeFrsPage>({
        list: [], totalRow: 0, pageSize: 25, pageNumber: 1, totalPage: 0, firstPage: true, lastPage: true,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success'
    });
    const [filterContent, setFilterContent] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailId, setDetailId] = useState<string | undefined>(undefined);

    // 懒加载状态
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [childrenMap, setChildrenMap] = useState<Record<string, SmeFrsVO[]>>({});
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

    const isSearchMode = filterContent.trim().length > 0;
    const initialLoadDone = useRef(false);

    const fetchData = useCallback(async (pageNumber = 1, pageSize = 25, content?: string) => {
        setLoading(true);
        try {
            const res = await AuditHkfrsSmeFrsService.getTreeList(pageNumber, pageSize, content || undefined);
            setData(res);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Failed to load data', severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, []);

    // 初始加载 + 翻页/每页条数变化
    useEffect(() => {
        setExpandedIds(new Set());
        setChildrenMap({});
        fetchData(page + 1, rowsPerPage, filterContent);
        initialLoadDone.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    // 防抖：过滤内容变化（跳过首次 mount 触发）
    useEffect(() => {
        if (!initialLoadDone.current) return;
        const handler = setTimeout(() => {
            setPage(0);
            setExpandedIds(new Set());
            setChildrenMap({});
            fetchData(1, rowsPerPage, filterContent);
        }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterContent]);

    const handleRemove = async (id: string) => {
        setLoading(true);
        try {
            await AuditHkfrsSmeFrsService.deleteById(id);
            setSnackbar({open: true, message: 'Deleted successfully!', severity: 'success'});
            fetchData(page + 1, rowsPerPage, filterContent);
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Delete failed!', severity: 'error'});
        } finally {
            setLoading(false);
        }
    };

    /** 懒加载模式：展开/折叠节点 */
    const toggleExpandLazy = async (id: string) => {
        if (expandedIds.has(id)) {
            setExpandedIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            return;
        }
        // 展开：如果子节点尚未加载则请求
        if (!childrenMap[id]) {
            setLoadingIds(prev => new Set(prev).add(id));
            try {
                const children = await AuditHkfrsSmeFrsService.getChildren(id);
                setChildrenMap(prev => ({...prev, [id]: children}));
                if (!children || children.length === 0) {
                    // 后端返回空子节点，标记为不可展开
                    setData(prev => ({
                        ...prev,
                        list: prev.list.map(r =>
                            r.id === id ? {...r, hasChildren: false} : r
                        ),
                    }));
                    // 同时更新 childrenMap 中父级节点下该节点的 hasChildren
                    setChildrenMap(prev => {
                        const updated = {...prev};
                        for (const key of Object.keys(updated)) {
                            updated[key] = updated[key].map(c =>
                                c.id === id ? {...c, hasChildren: false} : c
                            );
                        }
                        return updated;
                    });
                    return;
                }
            } catch (e: any) {
                setSnackbar({open: true, message: e?.message || 'Failed to load children', severity: 'error'});
                return;
            } finally {
                setLoadingIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        }
        setExpandedIds(prev => new Set(prev).add(id));
    };

    /** 搜索模式：纯前端展开/折叠 */
    const toggleExpandSearch = (id: string) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const openDetail = (id: string) => {
        setDetailId(id);
        setDialogOpen(true);
    };

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                p: 2,
                bgcolor: 'transparent',
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                       alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">SME-FRS Dictionary</Typography>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}
                           alignItems={{xs: 'stretch', sm: 'center'}}>
                        <InputField size="small" label="Search Content" value={filterContent} onChange={e => {
                            setFilterContent(e.target.value);
                        }}/>
                        <Button variant="contained" color="secondary"
                                sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                onClick={() => {
                                    setDetailId(undefined);
                                    setDialogOpen(true);
                                }}>Create</Button>
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            minWidth: 600,
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>Session</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Content</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Code</TableCell>
                                <TableCell sx={{fontWeight: 700, width: 120}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(rowsPerPage).keys()].map(i => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="text"/></TableCell>
                                    <TableCell><Skeleton variant="rounded" height={24}/></TableCell>
                                </TableRow>
                            ))}
                            {!loading && data.list.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {!loading && data.list.map((r, idx) => {
                                const canExpand = isSearchMode
                                    ? (r.children && r.children.length > 0)
                                    : r.hasChildren !== false;
                                const isExpanded = r.id ? expandedIds.has(r.id) : false;
                                const isNodeLoading = r.id ? loadingIds.has(r.id) : false;
                                return (
                                    <React.Fragment key={r.id ?? `row-${idx}`}>
                                        <TableRow hover
                                                  sx={{cursor: canExpand ? 'pointer' : 'default'}}
                                                  onClick={() => r.id && canExpand && (isSearchMode
                                                      ? toggleExpandSearch(r.id)
                                                      : toggleExpandLazy(r.id))}>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    {canExpand && (isNodeLoading
                                                        ? <CircularProgress size={16}/>
                                                        : isExpanded ? <ExpandLess fontSize="small"/> :
                                                            <ExpandMore fontSize="small"/>)}
                                                    <span>{r.no || '-'}</span>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{r.content || '-'}</TableCell>
                                            <TableCell>{(r.code ?? []).join(', ') || '-'}</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}
                                                       onClick={e => e.stopPropagation()}>
                                                    <IconButton size="small" color="primary" onClick={() => {
                                                        setDetailId(r.id);
                                                        setDialogOpen(true);
                                                    }}>
                                                        <InfoOutlined fontSize="small"/>
                                                    </IconButton>
                                                    {r.id && (
                                                        <DeletePopover nodeId={r.id}
                                                                       nodeName={r.content || r.no || ''}
                                                                       onRemove={handleRemove} theme={theme}/>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        {/* 搜索模式：使用预加载的子树 */}
                                        {isSearchMode && isExpanded && r.children && r.children.length > 0 && (
                                            <SearchChildRows children={r.children} depth={1} theme={theme}
                                                             onDetail={openDetail} onRemove={handleRemove}/>
                                        )}
                                        {/* 懒加载模式：使用 childrenMap */}
                                        {!isSearchMode && isExpanded && r.id && (
                                            <LazyChildRows parentId={r.id} depth={1} theme={theme}
                                                           childrenMap={childrenMap} expandedIds={expandedIds}
                                                           loadingIds={loadingIds} onToggle={toggleExpandLazy}
                                                           onDetail={openDetail} onRemove={handleRemove}/>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                    <TablePagination
                        component="div"
                        count={data.totalRow}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{flexShrink: 0}}
                    />

                <SmeFrsDetail
                    open={dialogOpen}
                    smeFrsId={detailId}
                    onClose={(p?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => {
                        setDialogOpen(false);
                        setDetailId(undefined);
                        if (p?.message) setSnackbar({
                            open: true,
                            message: p.message,
                            severity: p.severity || 'success'
                        });
                        if (p?.refreshed) {
                            setChildrenMap({});
                            setExpandedIds(new Set());
                            fetchData(page + 1, rowsPerPage, filterContent);
                        }
                    }}
                />
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                />
            </Box>
        </Slide>
    );
}
